import React, { useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Paragraph, Separator } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { getNestedSubelementsByType, getPersonName, useSlsHighlightsContext, useSlsStateContext } from '@fs/zion-sls'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'

/**
 * ReviewCardBody
 *
 * Displays instructions for the user to review entered details. Shows different messages
 * based on whether additional family members need details:
 *  - No family members: Prompts user to save and attach to tree.
 *  - Has family members: Shows list of pending people and Save and Continue button.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.person           SLS person object containing at least localizedName.
 * @param {Array<Object>}  [props.familyMembersToReview=[]] Array of new people objects containing id and localizedName.
 * @param {string} [props.splitScreenPartitionKey] PartitionKey of split screen image if active.
 * @returns {JSX.Element}
 */
function ReviewCardBody({ person, familyMembersToReview = [], splitScreenPartitionKey }) {
  const [t] = useTranslation()
  const { currentImageArk } = useSlsStateContext()
  const { setDisplayHighlights } = useSlsHighlightsContext()
  const { topMostPanel } = useOverlayContext()
  const topMostPanelId = useMemo(() => topMostPanel(), [topMostPanel])

  const hasNewPeople = familyMembersToReview.length > 0
  const personName = getPersonName({ person, justOneName: true })

  useEffect(() => {
    if (currentImageArk) {
      const fields = getNestedSubelementsByType(person, 'FIELD')
      const fieldIds = fields.map((field) => field.id)

      setDisplayHighlights({
        mainPartitionKey: currentImageArk,
        splitScreenPartitionKey,
        elementIds: fieldIds,
      })
    }
  }, [currentImageArk, splitScreenPartitionKey, topMostPanelId, setDisplayHighlights, person])

  return (
    <>
      {!hasNewPeople && (
        <Paragraph size="sm">
          {t(
            'review-card.body.no-family-members',
            '{ personName } is ready to add! If everything looks correct, you can save and attach to tree.',
            { personName }
          )}
        </Paragraph>
      )}
      {hasNewPeople && (
        <>
          <Paragraph size="sm">
            {t(
              'review-card.body.with-family-members',
              '{ personName } is ready to add! If everything looks correct, you will now add details for the following family members:',
              { personName }
            )}
          </Paragraph>
          <Separator size="xxs" />
          <ul>
            {familyMembersToReview.map((member) => (
              <li key={member?.id}>{getPersonName({ person: member, justOneName: true }) || ''}</li>
            ))}
          </ul>
        </>
      )}
    </>
  )
}

ReviewCardBody.propTypes = {
  person: PropTypes.shape({
    localizedName: PropTypes.string,
  }).isRequired,
  familyMembersToReview: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      localizedName: PropTypes.string.isRequired,
    })
  ),
  splitScreenPartitionKey: PropTypes.string,
}

ReviewCardBody.defaultProps = {
  familyMembersToReview: [],
}

export default ReviewCardBody
